import './index.css'
import './locale/i18n'

import { User } from '@abiglobalhealth/go-types'
import * as Sentry from '@sentry/react'
import { StrictMode } from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, redirect, RouterProvider } from 'react-router-dom'

import { getConfig } from '@/api/config'
import { getUser } from '@/api/user'
import { ConfigProvider } from '@/contexts/config'
import { Error } from '@/pages/Error'
import { Root } from '@/pages/Root'
import { initGTM } from '@/utils/gtm'

import { getEnvUrl } from './api/utils'

// Polyfill for Object.hasOwn. Fixes errors on Navigator markdown (https://abi-global-health-v0.sentry.io/issues/7158991907/)
if (!Object.hasOwn) {
  Object.defineProperty(Object, 'hasOwn', {
    value(object: object, property: string | symbol) {
      if (object == null) throw new TypeError('Cannot convert undefined or null to object')

      return Object.prototype.hasOwnProperty.call(object, property)
    },
    configurable: true,
    writable: true,
    enumerable: false
  })
}

const isHKPartner = (key: string) =>
  ['hcp-testing-hk', 'abi-internal-testing-hk', 'hsbc-hk', 'hsbc-hk-abi', 'hsbc-hk-test', 'azhk', 'azhk_test'].includes(key)

const router = createBrowserRouter([
  /*
    URLS format:
      http://go.abi.ai/<parnerKey>/<userId>?q=<consultationType>
      http://go.abi.ai/<partnerKey>?q=<consultationType>

  */
  {
    path: '/:key/:userId?',
    element: <Root />,
    errorElement: (
      <ConfigProvider>
        <Error />
      </ConfigProvider>
    ),
    loader: async ({ params: { key, userId }, request }) => {
      const url = new URL(request.url)
      Sentry.setTags({
        apiKey: key,
        userId
      })

      if (!key) throw new Response('Not Found', { status: 404 })

      const subdomain = url.hostname.split('.')[0]
      Sentry.setTag('region', subdomain)

      // TODO remove this when all HK partners are using the new subdomain
      if (isHKPartner(key) && subdomain.includes('goapp')) return redirect(url.href.replace('goapp', 'ape1'))

      localStorage.removeItem('config')
      const { config } = await getConfig()
      if (config) localStorage.setItem('config', JSON.stringify(config))

      const lsUser = localStorage.getItem('user')
      const user = lsUser ? (JSON.parse(lsUser) as User) : null

      const id = userId || user?.data.userId
      if (id) {
        const fetchedUser = await getUser({ userId: id })
        localStorage.setItem('user', JSON.stringify(fetchedUser))
      }

      return null
    }
  },
  {
    path: '/',
    element: (
      <ConfigProvider>
        <Error />
      </ConfigProvider>
    )
  },
  {
    path: '/error',
    element: (
      <ConfigProvider>
        <Error />
      </ConfigProvider>
    )
  }
])

// Do not initialize Sentry on localhost
if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
  // This is needed in order to difference between canary and prod (both are same .env)
  let environment
  if (import.meta.env.DEV || window.location.hostname.startsWith('dev')) {
    environment = 'development'
  } else if (window.location.hostname.startsWith('test')) {
    environment = 'test'
  } else if (window.location.hostname.startsWith('canary')) {
    environment = 'canary'
  } else {
    environment = 'production'
  }

  const sentryConfig: Sentry.BrowserOptions = {
    dsn: 'https://01072317f4bb127d500f9e66b5b0860b@o509172.ingest.sentry.io/4506179373957120',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllInputs: true,
        maskAllText: false,
        blockAllMedia: false,
        networkDetailAllowUrls: [`${getEnvUrl('VITE_API_URL')}/flow`],
        mask: ['.replay-mask'], // Mask all text elements with the class replay-mask
        block: ['.replay-block'] // Block all elements with the class replay-block
      })
    ],
    replaysSessionSampleRate: 0.05, // 5% of sessions (without errors) will be captured
    replaysOnErrorSampleRate: 1.0, // 100% of errors will be captured
    tracesSampleRate: 1.0,
    environment,
    release: import.meta.env.RELEASE,
    initialScope: {
      tags: {
        version: import.meta.env.APP_VERSION,
        buildDate: import.meta.env.BUILD_DATE
      }
    },
    ignoreErrors: [/Network Error/i, /Failed to fetch/i], // Ignore errors caused by network issues
    beforeSend(event, hint) {
      console.debug('🤖 Sending Sentry', event, hint)
      return event
    }
  }

  Sentry.init(sentryConfig)
}

if (import.meta.env.PROD && import.meta.env.VITE_GTM_ID) {
  initGTM(import.meta.env.VITE_GTM_ID)
}

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>
)
